import React, { useState, useEffect, useCallback } from 'react';
import { Filter, RefreshCw, X, Tag } from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import ThemeSelect from '../components/ThemeSelect';
import { attendanceApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';

// Import Custom SVG Icons
import TotalEmployeesIcon    from '../assets/Logo2.0Screen/Total Employees.svg';
import PresentEmployeesIcon  from '../assets/Logo2.0Screen/Present Employees.svg';
import AbsentEmployeesIcon   from '../assets/Logo2.0Screen/Absent Employees.svg';
import OnLeaveEmployeesIcon  from '../assets/Logo2.0Screen/On Leave Employees.svg';
import LoggedInIcon          from '../assets/Logo2.0Screen/Logged in.svg';

const formatTime = (t) => {
  if (!t) return '—';
  try {
    const [h, m] = t.split(':');
    const hours = parseInt(h, 10);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${String(displayHours).padStart(2, '0')}:${m} ${ampm}`;
  } catch { return t; }
};

const formatDesignation = (d) => {
  if (!d || d === 'null' || d === '—') return '—';
  if (typeof d === 'string' && d.includes('name=')) {
    const match = d.match(/name=([^,)]+)/);
    if (match && match[1]) return match[1].trim();
  }
  return d;
};

function DashboardRealtime() {
  const { currentTheme } = useTheme();

  // ── Stats ──────────────────────────────────────────────────────────────────
  const [stats, setStats] = useState({
    totalEmployees: 0,
    presentEmployees: 0,
    absentEmployees: 0,
    onLeaveEmployees: 0,
    loggedIn: 0,
  });

  // ── Employee list ──────────────────────────────────────────────────────────
  const [employees, setEmployees]       = useState([]);
  const [loading, setLoading]           = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  // ── Search / Filter / Pagination ───────────────────────────────────────────
  const [searchTerm, setSearchTerm]     = useState('');
  const [currentPage, setCurrentPage]   = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);  // ← from API now
  const [totalPages, setTotalPages]     = useState(1);  // ← from API now
  const pageSize                        = 10;

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    currentStatus: '',
    status: '',
    punchInStart: '',
    punchInEnd: '',
  });
  const [tempFilters, setTempFilters] = useState({ ...activeFilters });

  // ── Fetch Stats ─────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const data  = await attendanceApi.getAttendanceStats(today);
      setStats({
        totalEmployees:   data?.totalEmployees   ?? data?.total        ?? 0,
        presentEmployees: data?.presentEmployees ?? data?.presentCount ?? data?.present ?? 0,
        absentEmployees:  data?.absentEmployees  ?? data?.absentCount  ?? data?.absent  ?? 0,
        onLeaveEmployees: data?.onLeaveEmployees ?? data?.leaveCount   ?? data?.onLeave ?? 0,
        loggedIn:         data?.loggedInEmployees?? data?.onlineCount  ?? data?.online  ?? 0,
      });
    } catch (err) {
      console.warn('Stats fetch failed:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // ── Fetch Employee List ─────────────────────────────────────────────────────
const fetchEmployees = useCallback(async (search = '', page = 0, filters = {}) => {
    try {
      setLoading(true);

      const data = await attendanceApi.getRealtimeEmployees({
        search: search || undefined,
        page,
        size: pageSize,
      });

      let list  = [];
      let total = 0;
      let pages = 1;

      if (Array.isArray(data)) {
        list  = data;
        total = data.length;
        pages = 1;
      } else {
        // ✅ "employees" key fix + "totalCount" fix
        list  = data?.employees ?? data?.content ?? data?.data ?? [];
        total = data?.totalCount ?? data?.totalElements ?? list.length;
        pages = data?.totalPages ?? Math.max(1, Math.ceil(total / pageSize));
      }

      // ── Client-side filters (status, currentStatus, punchInTime) ────────────
      // These aren't supported by the new API so we filter locally on the page data.
      // Note: this only filters the current page — for full accuracy consider
      // fetching all and paginating locally, or ask backend to add filter params.
      if (filters.currentStatus || filters.status || filters.punchInStart || filters.punchInEnd) {
        const today = new Date().toISOString().split('T')[0];

        const normaliseStatus = (val) => {
          if (!val || typeof val !== 'string') return val;
          if (val.includes('name=')) return val.split('name=')[1].split(',')[0].replace(/[)]+$/, '');
          return val;
        };

        const toMins = (t) => {
          if (!t) return null;
          const [h, m] = t.split(':').map(Number);
          return h * 60 + m;
        };
        const startMins = filters.punchInStart ? toMins(filters.punchInStart) : null;
        const endMins   = filters.punchInEnd   ? toMins(filters.punchInEnd)   : null;

        list = list.filter(emp => {
          if (filters.currentStatus && normaliseStatus(emp.currentStatus) !== filters.currentStatus) return false;
          if (filters.status        && normaliseStatus(emp.status)        !== filters.status)        return false;
          if ((startMins !== null || endMins !== null) && !emp.punchInTime) return false;
          if (emp.punchInTime && (startMins !== null || endMins !== null)) {
            const localTime = new Date(`${today}T${emp.punchInTime}`);
            const empMins   = localTime.getHours() * 60 + localTime.getMinutes();
            if (startMins !== null && empMins < startMins) return false;
            if (endMins   !== null && empMins > endMins)   return false;
          }
          return true;
        });

        // Recalculate totals after client-side filter
        total = list.length;
        pages = Math.max(1, Math.ceil(total / pageSize));
      }

      setEmployees(list);
      setTotalEntries(total);
      setTotalPages(pages);
    } catch (err) {
      console.warn('Employee fetch failed:', err);
      message.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  // ── On mount ────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchStats();
    fetchEmployees(searchTerm, 0, activeFilters);
  }, []); // eslint-disable-line

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const handleRefresh = () => {
    const reset = { currentStatus: '', status: '', punchInStart: '', punchInEnd: '' };
    setTempFilters(reset);
    setActiveFilters(reset);
    setSearchTerm('');
    setCurrentPage(0);
    fetchStats();
    fetchEmployees('', 0, reset);
    message.success('Data refreshed!');
  };

  const handleApplyFilters = () => {
    setActiveFilters(tempFilters);
    setCurrentPage(0);
    setIsFilterModalOpen(false);
    fetchEmployees(searchTerm, 0, tempFilters);
  };

  const handleResetFilters = () => {
    const reset = { currentStatus: '', status: '', punchInStart: '', punchInEnd: '' };
    setTempFilters(reset);
    setActiveFilters(reset);
    setCurrentPage(0);
    setIsFilterModalOpen(false);
    fetchEmployees(searchTerm, 0, reset);
  };

  // Page change — calls API with new page number
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchEmployees(searchTerm, newPage, activeFilters);
  };

  // Search — reset to page 0 on new search
  const handleSearchChange = (v) => {
    setSearchTerm(v);
    setCurrentPage(0);
    fetchEmployees(v, 0, activeFilters);
  };

  const activeFilterCount  = Object.values(activeFilters).filter(Boolean).length;
  const safePage           = Math.min(currentPage, Math.max(0, totalPages - 1));
  const startEntry         = totalEntries === 0 ? 0 : safePage * pageSize + 1;
  const endEntry           = Math.min((safePage + 1) * pageSize, totalEntries);

  // ── Stat Cards ───────────────────────────────────────────────────────────────
  const isDark = ['Purple', 'Black'].includes(currentTheme.name);

  const statCards = [
    {
      label: 'Total Employees',
      value: stats.totalEmployees,
      theme: { 
        bg: isDark ? 'bg-blue-900/20' : 'bg-[#F0F7FF]', 
        border: isDark ? 'border-blue-500/30' : 'border-[#E0EFFF]', 
        inner: 'shadow-[inset_0px_0px_15px_rgba(37,99,235,0.12)]' 
      },
      icon: <img src={TotalEmployeesIcon} alt="Total" className="w-full h-full object-contain" />,
    },
    {
      label: 'Present Employees',
      value: stats.presentEmployees,
      theme: { 
        bg: isDark ? 'bg-emerald-900/20' : 'bg-[#F0FDF4]', 
        border: isDark ? 'border-emerald-500/30' : 'border-[#DCFCE7]', 
        inner: 'shadow-[inset_0px_0px_15px_rgba(5,150,105,0.12)]' 
      },
      icon: <img src={PresentEmployeesIcon} alt="Present" className="w-full h-full object-contain" />,
    },
    {
      label: 'Absent Employees',
      value: stats.absentEmployees,
      theme: { 
        bg: isDark ? 'bg-rose-900/20' : 'bg-[#FFF1F2]', 
        border: isDark ? 'border-rose-500/30' : 'border-[#FFE4E6]', 
        inner: 'shadow-[inset_0px_0px_15px_rgba(225,29,72,0.12)]' 
      },
      icon: <img src={AbsentEmployeesIcon} alt="Absent" className="w-full h-full object-contain" />,
    },
    {
      label: 'On Leave Employees',
      value: stats.onLeaveEmployees,
      theme: { 
        bg: isDark ? 'bg-amber-900/20' : 'bg-[#FFFBEB]', 
        border: isDark ? 'border-amber-500/30' : 'border-[#FEF3C7]', 
        inner: 'shadow-[inset_0px_0px_15px_rgba(217,119,6,0.12)]' 
      },
      icon: <img src={OnLeaveEmployeesIcon} alt="On Leave" className="w-full h-full object-contain" />,
    },
    {
      label: 'Logged In',
      value: stats.loggedIn,
      theme: { 
        bg: isDark ? 'bg-cyan-900/20' : 'bg-[#ECFEFF]', 
        border: isDark ? 'border-cyan-500/30' : 'border-[#CFFAFE]', 
        inner: 'shadow-[inset_0px_0px_15px_rgba(8,145,178,0.12)]' 
      },
      icon: <img src={LoggedInIcon} alt="Logged in" className="w-full h-full object-contain" />,
    },
  ];

  // ── Badges ───────────────────────────────────────────────────────────────────
  const CurrentStatusBadge = ({ status }) => {
    const isDark = ['Purple', 'Black'].includes(currentTheme.name);
    const map = {
      ONLINE:      { text: 'Online',      cls: isDark ? 'text-green-400 bg-green-900/30' : 'text-green-600 bg-green-50' },
      OFFLINE:     { text: 'Offline',     cls: isDark ? 'text-red-400 bg-red-900/30' : 'text-red-500 bg-red-50' },
      LUNCH_BREAK: { text: 'Lunch Break', cls: isDark ? 'text-orange-400 bg-orange-900/30' : 'text-orange-500 bg-orange-50' },
      TEA_BREAK:   { text: 'Tea Break',   cls: isDark ? 'text-amber-400 bg-amber-900/30' : 'text-amber-600 bg-amber-50' },
    };
    const s   = (typeof status === 'string' && status.includes('name=')) ? status.split('name=')[1].split(',')[0].replace(/[)]+$/, '') : status;
    const cfg = map[s] || { text: s || '—', cls: isDark ? 'text-gray-300 bg-gray-800/50' : 'text-gray-500 bg-gray-50' };
    return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cfg.cls}`}>{cfg.text || s}</span>;
  };

  const AttendanceBadge = ({ status }) => {
    const isDark = ['Purple', 'Black'].includes(currentTheme.name);
    const map = {
      PRESENT:        { text: 'Present',  cls: isDark ? 'text-green-400 bg-green-900/30' : 'text-green-700 bg-green-50' },
      ABSENT:         { text: 'Absent',   cls: isDark ? 'text-red-400 bg-red-900/30' : 'text-red-600 bg-red-50' },
      ON_LEAVE:       { text: 'On Leave', cls: isDark ? 'text-purple-400 bg-purple-900/30' : 'text-purple-600 bg-purple-50' },
      HALF_DAY:       { text: 'Half Day', cls: isDark ? 'text-yellow-400 bg-yellow-900/30' : 'text-yellow-600 bg-yellow-50' },
      WORK_FROM_HOME: { text: 'WFH',      cls: isDark ? 'text-indigo-400 bg-indigo-900/30' : 'text-indigo-600 bg-indigo-50' },
    };
    const s   = (typeof status === 'string' && status.includes('name=')) ? status.split('name=')[1].split(',')[0].replace(/[)]+$/, '') : status;
    const cfg = map[s] || { text: s || '—', cls: isDark ? 'text-gray-300 bg-gray-800/50' : 'text-gray-500 bg-gray-50' };
    return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cfg.cls}`}>{cfg.text || s}</span>;
  };

  return (
    <div className="w-full space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-[28px] font-black text-[#1a3b32]">Real-time</h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { setTempFilters(activeFilters); setIsFilterModalOpen(true); }}
            className="relative p-2.5 bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 transition-all text-[#449978]"
            title="Filter"
          >
            <Filter className="w-5 h-5" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-black text-white flex items-center justify-center"
                style={{ backgroundColor: currentTheme.color }}>
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-2 rounded-lg flex items-center justify-center transition-all hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: currentTheme.color }}
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 text-white ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div className="bg-white border-2 border-gray-100 rounded-[24px] p-5 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 w-full">
          {statCards.map((stat, i) => (
            <div
              key={i}
              className={`flex items-center px-4 rounded-[24px] border-2 h-[92px] w-full transition-all shadow-sm hover:shadow-lg hover:-translate-y-0.5 ${stat.theme.bg} ${stat.theme.border} ${stat.theme.inner}`}
            >
              <div className="w-14 h-14 flex items-center justify-center shrink-0">{stat.icon}</div>
              <div className="ml-3 flex flex-col justify-center min-w-0">
                <p className="text-[11px] font-black text-gray-400 uppercase tracking-tight truncate">{stat.label}</p>
                <p className="text-[24px] font-black text-gray-800 leading-none mt-1">
                  {statsLoading ? '—' : stat.value}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Search ── */}
      <ExpandableSearchBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        placeholder="Search by employee name or code"
      />

      {/* ── Table ── */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ background: currentTheme.color }}>
              <tr>
                {['Employee', 'Designation', 'IP Address', 'Attendance', 'Current Status',
                  'Punch In', 'Punch Out', 'Lunch Break', 'Tea Breaks', 'Total Break', 'Late'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-[10px] font-bold text-white uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
                        style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }} />
                      <p className="text-sm text-gray-400 font-semibold">Loading employees...</p>
                    </div>
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-16 text-center text-gray-400 text-sm font-semibold">
                    No employees found
                  </td>
                </tr>
              ) : (
                employees.map((emp, idx) => (
                  <tr
                    key={emp.id || idx}
                    style={{ backgroundColor: idx % 2 === 0 ? (isDark ? '#1e293b' : 'white') : (isDark ? '#0f172a' : `${currentTheme.color}08`) }}
                  >
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-bold text-gray-800">{emp.employeeName || emp.name || '—'}</p>
                        <p className="text-[10px] text-gray-400">{emp.employeeCode || ''}</p>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap max-w-[200px] truncate">
                      {(() => {
                        const d = emp.designation;
                        if (!d || d === 'null' || d === '_' || d === 'undefined') return '—';
                        if (typeof d === 'string' && d.includes('name=')) return d.split('name=')[1].split(',')[0].split(')')[0];
                        return d.name || d;
                      })()}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap font-mono">
                      {(emp.punchInIpAddress && emp.punchInIpAddress !== '_' && emp.punchInIpAddress !== 'null')
                        ? emp.punchInIpAddress
                        : (emp.ipAddress && emp.ipAddress !== '_' && emp.ipAddress !== 'null' ? emp.ipAddress : '—')}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap"><AttendanceBadge status={emp.status} /></td>
                    <td className="px-5 py-4 whitespace-nowrap"><CurrentStatusBadge status={emp.currentStatus} /></td>
                    <td className="px-5 py-4 text-sm text-gray-600 whitespace-nowrap font-medium">
                      {emp.punchInTime ? formatTime(emp.punchInTime) : '—'}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600 whitespace-nowrap font-medium">
                      {emp.punchOutTime ? formatTime(emp.punchOutTime) : '—'}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {emp.lunchBreakStart ? (
                        <div className="flex flex-col gap-0.5">
                          <span className={`text-[11px] ${isDark ? 'text-orange-400 bg-orange-900/30' : 'text-orange-600 bg-orange-50'} font-semibold px-1.5 py-0.5 rounded`}>
                            {formatTime(emp.lunchBreakStart)} – {emp.lunchBreakEnd ? formatTime(emp.lunchBreakEnd) : 'ongoing'}
                          </span>
                          {emp.lunchBreakDurationFormatted && emp.lunchBreakDurationFormatted !== '00:00:00' && (
                            <span className="text-[10px] text-gray-400">{emp.lunchBreakDurationFormatted}</span>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {emp.totalTeaBreakCount > 0 ? (
                        <div className="flex flex-col gap-0.5">
                          <span className={`text-[11px] ${isDark ? 'text-amber-400 bg-amber-900/30' : 'text-amber-600 bg-amber-50'} font-semibold px-1.5 py-0.5 rounded`}>
                            ×{emp.totalTeaBreakCount}
                          </span>
                          {emp.totalTeaBreakDurationFormatted && emp.totalTeaBreakDurationFormatted !== '00:00:00' && (
                            <span className="text-[10px] text-gray-400">{emp.totalTeaBreakDurationFormatted}</span>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {emp.totalBreakDurationFormatted && emp.totalBreakDurationFormatted !== '00:00:00'
                        ? <span className={`${isDark ? 'bg-gray-800/50 text-gray-300' : 'bg-gray-50 text-gray-600'} px-1.5 py-0.5 rounded text-[11px] font-semibold`}>{emp.totalBreakDurationFormatted}</span>
                        : '—'}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      {emp.isLate
                        ? <span className={`text-[11px] font-bold ${isDark ? 'text-red-400 bg-red-900/30' : 'text-red-500 bg-red-50'} px-2 py-0.5 rounded`}>
                            {emp.lateByMinutes ? `${emp.lateByMinutes}m late` : 'Late'}
                          </span>
                        : <span className={`text-[11px] font-bold ${isDark ? 'text-green-400 bg-green-900/30' : 'text-green-600 bg-green-50'} px-2 py-0.5 rounded`}>On Time</span>
                      }
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-100">
          <p className="text-sm text-gray-600 font-medium">
            Showing {startEntry}–{endEntry} of {totalEntries} entries
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => handlePageChange(safePage - 1)}
              disabled={safePage === 0}
              className="px-3 py-1.5 rounded border text-sm disabled:opacity-40 disabled:cursor-not-allowed bg-white hover:bg-gray-50 transition-colors"
            >←</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i).map(p => (
              <button
                key={p}
                onClick={() => handlePageChange(p)}
                className="px-3 py-1.5 rounded border text-sm transition-colors"
                style={{
                  backgroundColor: safePage === p ? currentTheme.color : 'white',
                  color:           safePage === p ? 'white'             : '#374151',
                  borderColor:     safePage === p ? currentTheme.color  : '#D1D5DB',
                }}
              >{p + 1}</button>
            ))}
            <button
              onClick={() => handlePageChange(safePage + 1)}
              disabled={safePage >= totalPages - 1}
              className="px-3 py-1.5 rounded border text-sm disabled:opacity-40 disabled:cursor-not-allowed bg-white hover:bg-gray-50 transition-colors"
            >→</button>
          </div>
        </div>
      </div>

      {/* ── Filter Modal ── */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-xl w-full max-w-md mx-4 overflow-hidden shadow-xl border ${isDark ? 'bg-[#1E293B] border-white/10' : 'bg-white border-transparent'}`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`} style={{ background: currentTheme.color }}>
              <h2 className="text-lg font-bold text-white">Filter Employees</h2>
              <button onClick={() => setIsFilterModalOpen(false)} className="p-1 hover:bg-white/20 rounded transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Current Status */}
              <div>
                <label className={`block text-sm font-bold mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Current Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { val: '',            label: 'All' },
                    { val: 'ONLINE',      label: 'Online' },
                    { val: 'OFFLINE',     label: 'Offline' },
                    { val: 'LUNCH_BREAK', label: 'Lunch Break' },
                    { val: 'TEA_BREAK',   label: 'Tea Break' },
                  ].map(opt => (
                    <button
                      key={opt.val}
                      onClick={() => setTempFilters(f => ({ ...f, currentStatus: opt.val }))}
                      className="px-3 py-2 rounded-lg border text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: tempFilters.currentStatus === opt.val ? `${currentTheme.color}18` : (isDark ? '#0F172A' : 'white'),
                        borderColor:     tempFilters.currentStatus === opt.val ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'),
                        color:           tempFilters.currentStatus === opt.val ? currentTheme.color : (isDark ? '#9ca3af' : '#4B5563'),
                      }}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
              {/* Attendance Status */}
              <div>
                <label className={`block text-sm font-bold mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Attendance Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { val: '',               label: 'All' },
                    { val: 'PRESENT',        label: 'Present' },
                    { val: 'ABSENT',         label: 'Absent' },
                    { val: 'ON_LEAVE',       label: 'On Leave' },
                    { val: 'HALF_DAY',       label: 'Half Day' },
                    { val: 'WORK_FROM_HOME', label: 'WFH' },
                  ].map(opt => (
                    <button
                      key={opt.val}
                      onClick={() => setTempFilters(f => ({ ...f, status: opt.val }))}
                      className="px-3 py-2 rounded-lg border text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: tempFilters.status === opt.val ? `${currentTheme.color}18` : (isDark ? '#0F172A' : 'white'),
                        borderColor:     tempFilters.status === opt.val ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'),
                        color:           tempFilters.status === opt.val ? currentTheme.color : (isDark ? '#9ca3af' : '#4B5563'),
                      }}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
              {/* Punch In Time Range */}
              <div>
                <label className={`block text-sm font-bold mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Punch In Time</label>
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={tempFilters.punchInStart}
                    onChange={e => setTempFilters(f => ({ ...f, punchInStart: e.target.value }))}
                    className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none ${isDark ? 'bg-[#0f172a] border-white/10 text-white css-dark-time' : 'border-gray-300 text-black'}`}
                    style={isDark ? { colorScheme: 'dark' } : {}}
                  />
                  <span className={`font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>–</span>
                  <input
                    type="time"
                    value={tempFilters.punchInEnd}
                    onChange={e => setTempFilters(f => ({ ...f, punchInEnd: e.target.value }))}
                    className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none ${isDark ? 'bg-[#0f172a] border-white/10 text-white css-dark-time' : 'border-gray-300 text-black'}`}
                    style={isDark ? { colorScheme: 'dark' } : {}}
                  />
                </div>
              </div>
            </div>
            <div className={`flex justify-end px-6 py-4 border-t gap-3 ${isDark ? 'bg-[#0F172A] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
              <button
                onClick={handleResetFilters}
                className={`px-4 py-2 text-sm font-semibold border rounded-lg transition-colors ${isDark ? 'text-gray-300 bg-[#1E293B] border-white/10 hover:bg-white/5' : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'}`}
              >Reset</button>
              <button
                onClick={handleApplyFilters}
                className="px-5 py-2 text-sm font-semibold text-white rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.color }}
              >Apply Filters</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardRealtime;